import { useEffect, useState, type FormEvent } from "react";
import {
  Alert,
  AlertDescription,
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldDescription,
  FieldLabel,
  Label,
  RadioGroup,
  RadioGroupItem,
  Textarea,
} from "@diametral/design-system/react";

import { useCreateRequest } from "../../api/requests";
import type { ProjectDetail, Stream } from "../../api/types";
import { SelectField } from "../../components/SelectField";
import { useTranslation } from "../../lib/i18n";
import { notifySuccess } from "../../lib/toast";

const MESSAGE_MAX = 4000;

/** Ask the DAR to join one stream of the project. */
export function JoinDialog({
  open,
  onOpenChange,
  project,
  streams,
  blockedReason,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  project: ProjectDetail;
  /** Active streams the person is not assigned to yet. */
  streams: Stream[];
  blockedReason: string | null;
}) {
  const { t } = useTranslation();
  const [streamId, setStreamId] = useState("");
  const [message, setMessage] = useState("");
  const create = useCreateRequest();

  useEffect(() => {
    if (open) {
      setStreamId(streams.length === 1 ? String(streams[0]?.id) : "");
      setMessage("");
    }
    // Reset on open only: a background refetch of the project must not wipe the form.
  }, [open]);

  const stream = streams.find((s) => String(s.id) === streamId);

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!stream || blockedReason) return;
    create.mutate(
      { kind: "join", project: project.slug, stream_id: stream.id, message: message.trim() },
      {
        onSuccess: () => {
          notifySuccess(t("Request sent"), t("The DAR will handle it; the project's TL/PO have been notified."));
          onOpenChange(false);
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={(next: boolean) => onOpenChange(next)}>
      <DialogContent className="ada-dialog">
        <DialogHeader>
          <DialogTitle>{t("Ask to join {{title}}", { title: project.title })}</DialogTitle>
          <DialogDescription>
            {t("You join one stream of the project. The resource allocation office decides and sets the effective date.")}
          </DialogDescription>
        </DialogHeader>
        <form className="ada-form" onSubmit={submit} noValidate>
          {blockedReason ? (
            <Alert tone="warning">
              <AlertDescription>{blockedReason}</AlertDescription>
            </Alert>
          ) : null}
          <Field>
            <FieldLabel htmlFor="join-stream">{t("Stream")}</FieldLabel>
            <SelectField
              id="join-stream"
              label={t("Stream to join")}
              placeholder={t("Choose a stream")}
              value={streamId}
              onChange={setStreamId}
              options={streams.map((s) => ({ value: String(s.id), label: s.title }))}
              disabled={Boolean(blockedReason)}
            />
            {stream?.description ? <FieldDescription>{stream.description}</FieldDescription> : null}
          </Field>
          <Field>
            <FieldLabel htmlFor="join-message">{t("Message (optional)")}</FieldLabel>
            <Textarea
              id="join-message"
              rows={4}
              maxLength={MESSAGE_MAX}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={t("What you can bring, your availability…")}
              disabled={Boolean(blockedReason)}
            />
          </Field>
          <DialogFooter>
            <DialogClose render={<Button variant="outline" type="button" />}>{t("Cancel")}</DialogClose>
            <Button type="submit" variant="primary" loading={create.isPending} disabled={!stream || Boolean(blockedReason)}>
              {t("Send the request")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Ask the DAR to leave one stream, or the whole project. A person can always ask to leave. */
export function LeaveDialog({
  open,
  onOpenChange,
  project,
  myStreams,
  blockedReason,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  project: ProjectDetail;
  /** Streams the person is assigned to (current or upcoming). */
  myStreams: Stream[];
  blockedReason: string | null;
}) {
  const { t } = useTranslation();
  const [scope, setScope] = useState("project");
  const [message, setMessage] = useState("");
  const create = useCreateRequest();

  useEffect(() => {
    if (open) {
      setScope(myStreams.length > 1 ? "project" : myStreams[0] ? String(myStreams[0].id) : "project");
      setMessage("");
    }
    // Reset on open only: a background refetch of the project must not wipe the form.
  }, [open]);

  function submit(event: FormEvent) {
    event.preventDefault();
    if (blockedReason) return;
    create.mutate(
      {
        kind: "leave",
        project: project.slug,
        stream_id: scope === "project" ? null : Number(scope),
        message: message.trim(),
      },
      {
        onSuccess: () => {
          notifySuccess(t("Request sent"), t("The DAR will set the end date of your assignment."));
          onOpenChange(false);
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={(next: boolean) => onOpenChange(next)}>
      <DialogContent className="ada-dialog">
        <DialogHeader>
          <DialogTitle>{t("Ask to leave {{title}}", { title: project.title })}</DialogTitle>
          <DialogDescription>{t("The resource allocation office decides and sets the effective date.")}</DialogDescription>
        </DialogHeader>
        <form className="ada-form" onSubmit={submit} noValidate>
          {blockedReason ? (
            <Alert tone="warning">
              <AlertDescription>{blockedReason}</AlertDescription>
            </Alert>
          ) : null}
          <fieldset className="ada-stack ada-stack--tight" style={{ border: 0, padding: 0, margin: 0 }}>
            <legend className="ds-label">{t("Leave")}</legend>
            <RadioGroup value={scope} onValueChange={(value: unknown) => setScope(String(value))} disabled={Boolean(blockedReason)}>
              {myStreams.length > 1 || myStreams.length === 0 ? (
                <div className="ada-check">
                  <RadioGroupItem id="leave-project" value="project" />
                  <Label htmlFor="leave-project">{t("Whole project")}</Label>
                </div>
              ) : null}
              {myStreams.map((s) => (
                <div className="ada-check" key={s.id}>
                  <RadioGroupItem id={`leave-${s.id}`} value={String(s.id)} />
                  <Label htmlFor={`leave-${s.id}`}>{t("The stream “{{name}}”", { name: s.title })}</Label>
                </div>
              ))}
            </RadioGroup>
          </fieldset>
          <Field>
            <FieldLabel htmlFor="leave-message">{t("Message (optional)")}</FieldLabel>
            <Textarea
              id="leave-message"
              rows={4}
              maxLength={MESSAGE_MAX}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={t("The context, the desired date…")}
              disabled={Boolean(blockedReason)}
            />
          </Field>
          <DialogFooter>
            <DialogClose render={<Button variant="outline" type="button" />}>{t("Cancel")}</DialogClose>
            <Button type="submit" variant="primary" loading={create.isPending} disabled={Boolean(blockedReason)}>
              {t("Send the request")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
